import { Observable } from 'rxjs';
import { PagedList } from '../data/models/paged-list.model';
import { Driver } from '../data/models/driver.model';
import { SortModel } from '../data/models/sort-model.model';
import httpService from './httpService';
import { DriverDocument } from '../data/models/document.model';

class DriverService {
  private readonly apiBase;

  constructor() {
    const { __environment__ } = window as any;
    this.apiBase = __environment__.REACT_APP_DRIVER_API_URL.trim();
  }

  public findDrivers(
    pageSize: number,
    page: number,
    filter: string,
    search: string,
    sortModel: SortModel | null,
    onboarded: boolean,
    providerId?: number,
  ): Observable<PagedList<Driver>> {
    const f = encodeURIComponent(filter);
    const s = encodeURIComponent(search);

    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    const isEmailValid = emailRegex.test(search);

    const pwdFlag = onboarded
      ? '&isPasswordPermanent=true'
      : '&isPasswordTemporary=true';

    const searchFlag = isEmailValid
      ? `&email=${encodeURIComponent(search)}`
      : `&search=${s}`;
    let url = `${this.apiBase}/optimized?driverState=active,inactive&page=${page}&pageSize=${pageSize}&filter=${f}${searchFlag}${pwdFlag}`;
    if (sortModel) {
      url += `&sort=${sortModel.column}&sortOrder=${sortModel.direction}`;
    }
    if (providerId) {
      url += `&provider=${providerId}`;
    }
    return httpService.getJSON(url);
  }

  public getDriver(id: number): Observable<Driver> {
    return httpService.getJSON(`${this.apiBase}/${id}`);
  }

  public getDriversByProvider(
    providerId: number,
  ): Observable<PagedList<Driver>> {
    return httpService.getJSON(
      `${this.apiBase}/optimized?provider=${providerId}&pageSize=1000`,
    );
  }

  public createDriver(driver: Driver | any): Observable<Driver> {
    const url = this.apiBase;
    return httpService.postJSON(url, driver);
  }

  public updateDriver(driver: Driver): Observable<Driver> {
    const url = `${this.apiBase}/${driver.driverID}`;
    return httpService.putJSON(url, driver);
  }

  public deleteDriver(driverId: number): Observable<Driver> {
    const url = `${this.apiBase}/${driverId}`;
    return httpService.deleteJSON(url);
  }

  public uploadDriverDocument(
    driverId: number,
    document: DriverDocument,
    file: any,
  ): Observable<DriverDocument> {
    const url = `${this.apiBase}/${driverId}/documents`;
    const formData = new FormData();
    formData.append('document', file, file.name);
    formData.append('driverDocumentTypeID', '' + document.driverDocumentTypeID);
    if (document.signedOnDate) {
      formData.append('signedOnDate', document.signedOnDate);
    }
    if (document.effectiveStartDate) {
      formData.append('effectiveStartDate', document.effectiveStartDate);
    }
    if (document.effectiveEndDate) {
      formData.append('effectiveEndDate', document.effectiveEndDate);
    }

    return httpService.postJSON(url, formData, true);
  }

  public updateDriverDocument(
    providerId: number,
    document: DriverDocument,
  ): Observable<DriverDocument> {
    const url = `${this.apiBase}/${providerId}/documents/${document.driverDocumentID}`;
    return httpService.putJSON(url, document);
  }

  public deleteDriverDocument(
    providerId: number,
    documentId: number,
  ): Observable<DriverDocument> {
    const url = `${this.apiBase}/${providerId}/documents/${documentId}`;
    return httpService.deleteJSON(url);
  }

  public getDriverDocuments(driverId: number): Observable<DriverDocument[]> {
    const url = `${this.apiBase}/${driverId}/documents`;
    return httpService.getJSON(url);
  }

  public importDrivers(providerId: number, driverFile: any): Observable<void> {
    const url = `${this.apiBase}/bulk`;
    const formData = new FormData();
    formData.append('document', driverFile, driverFile.name);
    formData.append('ProviderID', '' + providerId);
    return httpService.postJSON(url, formData, true);
  }

  public sendActivationEmails(ids: any[]) {
    const url = `${this.apiBase}/resend-cognito-activation-emails`;
    return httpService.postJSON(
      url,
      ids,
      // .map((item: any) => {
      //   return { DriverID: item };
      // }),
    );
  }
}

const driverService = new DriverService();

export default driverService;
